'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const TOTAL_PAGES = 5;

// Luxury animation styles injected once
const luxuryStyles = `
  @keyframes lux-fade-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes lux-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes lux-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes lux-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-4px); }
  }
  .lux-heading {
    animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) both;
  }
  .lux-heading-delay {
    animation: lux-fade-up 0.7s cubic-bezier(0.22,1,0.36,1) 0.12s both;
  }
  .lux-card-1 {
    animation: lux-fade-up 0.6s cubic-bezier(0.22,1,0.36,1) 0.10s both;
  }
  .lux-card-2 {
    animation: lux-fade-up 0.6s cubic-bezier(0.22,1,0.36,1) 0.20s both;
  }
  .lux-card-3 {
    animation: lux-fade-up 0.6s cubic-bezier(0.22,1,0.36,1) 0.30s both;
  }
  .lux-card-4 {
    animation: lux-fade-up 0.6s cubic-bezier(0.22,1,0.36,1) 0.40s both;
  }
  .lux-card-5 {
    animation: lux-fade-up 0.6s cubic-bezier(0.22,1,0.36,1) 0.50s both;
  }
  .lux-shimmer-text {
    background: linear-gradient(90deg, #c9a96e 0%, #f5e0a8 40%, #c9a96e 60%, #e8c98a 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: lux-shimmer 3.5s linear infinite;
  }
  .lux-float {
    animation: lux-float 4s ease-in-out infinite;
  }
  .lux-fade-in {
    animation: lux-fade-in 1s ease both;
  }

  @keyframes petal-fall-1 {
    0%   { transform: translateY(-80px) translateX(0px) rotate(0deg); opacity: 0; }
    5%   { opacity: 0.85; }
    25%  { transform: translateY(22vh) translateX(30px) rotate(80deg); }
    50%  { transform: translateY(46vh) translateX(55px) rotate(175deg); }
    75%  { transform: translateY(70vh) translateX(35px) rotate(270deg); }
    95%  { opacity: 0.6; }
    100% { transform: translateY(105vh) translateX(60px) rotate(360deg); opacity: 0; }
  }
  @keyframes petal-fall-2 {
    0%   { transform: translateY(-80px) translateX(0px) rotate(25deg); opacity: 0; }
    5%   { opacity: 0.8; }
    30%  { transform: translateY(28vh) translateX(-40px) rotate(-90deg); }
    55%  { transform: translateY(52vh) translateX(-65px) rotate(-190deg); }
    80%  { transform: translateY(76vh) translateX(-45px) rotate(-280deg); }
    95%  { opacity: 0.55; }
    100% { transform: translateY(105vh) translateX(-55px) rotate(-350deg); opacity: 0; }
  }
  @keyframes petal-fall-3 {
    0%   { transform: translateY(-80px) translateX(0px) rotate(15deg); opacity: 0; }
    5%   { opacity: 0.9; }
    20%  { transform: translateY(18vh) translateX(20px) rotate(60deg); }
    45%  { transform: translateY(42vh) translateX(-10px) rotate(150deg); }
    70%  { transform: translateY(66vh) translateX(25px) rotate(240deg); }
    95%  { opacity: 0.65; }
    100% { transform: translateY(105vh) translateX(15px) rotate(330deg); opacity: 0; }
  }
  @keyframes petal-fall-4 {
    0%   { transform: translateY(-80px) translateX(0px) rotate(-20deg); opacity: 0; }
    5%   { opacity: 0.75; }
    35%  { transform: translateY(32vh) translateX(-30px) rotate(-110deg); }
    60%  { transform: translateY(58vh) translateX(15px) rotate(-220deg); }
    85%  { transform: translateY(82vh) translateX(-20px) rotate(-310deg); }
    95%  { opacity: 0.5; }
    100% { transform: translateY(105vh) translateX(-10px) rotate(-380deg); opacity: 0; }
  }
  @keyframes petal-fall-5 {
    0%   { transform: translateY(-80px) translateX(0px) rotate(40deg); opacity: 0; }
    5%   { opacity: 0.7; }
    28%  { transform: translateY(25vh) translateX(50px) rotate(130deg); }
    52%  { transform: translateY(50vh) translateX(70px) rotate(230deg); }
    78%  { transform: translateY(75vh) translateX(45px) rotate(320deg); }
    95%  { opacity: 0.45; }
    100% { transform: translateY(105vh) translateX(55px) rotate(400deg); opacity: 0; }
  }
  @keyframes petal-sway {
    0%, 100% { margin-left: 0px; }
    33%       { margin-left: 8px; }
    66%       { margin-left: -8px; }
  }
  @keyframes gold-float {
    0%   { transform: translateY(-20px) translateX(0px) scale(1); opacity: 0; }
    8%   { opacity: 0.7; }
    50%  { transform: translateY(50vh) translateX(20px) scale(0.85); opacity: 0.5; }
    92%  { opacity: 0.2; }
    100% { transform: translateY(105vh) translateX(30px) scale(0.7); opacity: 0; }
  }
  .petal-particle {
    position: absolute;
    top: 0;
    pointer-events: none;
    will-change: transform, opacity;
  }
  .petal-particle-close {
    filter: drop-shadow(0px 3px 5px rgba(120,60,60,0.18));
  }
  .petal-particle-far {
    filter: blur(0.8px);
  }
  .gold-particle {
    position: absolute;
    top: 0;
    pointer-events: none;
    will-change: transform, opacity;
    border-radius: 50%;
    background: radial-gradient(circle, #f5e0a8 0%, #c9a96e 60%, transparent 100%);
  }
`;

// ─── Premium Petal Overlay Configuration ───────────────────────────────────
const PETAL_COUNT = 35;
const PETAL_SPEED = "slow"; // "slow" | "medium" | "fast"

const SPEED_MAP: Record<string, number> = { slow: 1.6, medium: 0.65, fast: 0.4 };

// Petal palette: blush pink, burgundy, ivory, soft rose
const PETAL_PALETTE = [
  // Blush pink
  { outer: '#f2b8c6', inner: '#fde8ef', vein: '#e8a0b4' },
  // Soft rose
  { outer: '#e8a0b4', inner: '#fcd5e0', vein: '#d4849c' },
  // Burgundy/maroon
  { outer: '#8b2252', inner: '#c45c82', vein: '#6b1a3e' },
  // Deep burgundy
  { outer: '#7a1c48', inner: '#b04870', vein: '#5a1234' },
  // Ivory/cream
  { outer: '#f5ede0', inner: '#fdf8f0', vein: '#e8d8c0' },
  // Warm ivory
  { outer: '#f0e4d0', inner: '#faf4e8', vein: '#ddd0b8' },
];

const ANIM_VARIANTS = ['petal-fall-1', 'petal-fall-2', 'petal-fall-3', 'petal-fall-4', 'petal-fall-5'];

function buildPetalConfigs(count: number, speedKey: string) {
  const speedMult = SPEED_MAP[speedKey] ?? 1.0;
  const configs = [];
  for (let i = 0; i < count; i++) {
    const palette = PETAL_PALETTE[i % PETAL_PALETTE.length];
    const anim = ANIM_VARIANTS[i % ANIM_VARIANTS.length];
    // Depth layer: 0=close, 1=mid, 2=far
    const depth = i % 3;
    const baseSize = depth === 0 ? 14 + (i % 5) : depth === 1 ? 10 + (i % 4) : 7 + (i % 3);
    const baseDuration = depth === 0 ? 11 + (i % 5) : depth === 1 ? 14 + (i % 6) : 17 + (i % 5);
    const duration = (baseDuration * speedMult).toFixed(1) + 's';
    const delay = ((i * 0.72) % 12).toFixed(1) + 's';
    const left = ((i * 97 / count + (i % 7) * 3) % 98).toFixed(1) + '%';
    const opacity = depth === 0 ? 0.45 + (i % 3) * 0.04 : depth === 1 ? 0.30 + (i % 3) * 0.04 : 0.18 + (i % 3) * 0.04;
    configs.push({ palette, anim, size: baseSize, left, delay, duration, depth, opacity });
  }
  return configs;
}

interface PetalConfig {
  palette: { outer: string; inner: string; vein: string };
  anim: string;
  size: number;
  left: string;
  delay: string;
  duration: string;
  depth: number;
  opacity: number;
}

function PetalSVG({ palette, size }: { palette: { outer: string; inner: string; vein: string }; size: number }) {
  const uid = `p-${size}-${palette.outer.slice(1, 4)}`;
  return (
    <svg width={size * 2.2} height={size * 2.5} viewBox="0 0 26 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id={uid} cx="38%" cy="28%" r="68%">
          <stop offset="0%" stopColor={palette.inner} stopOpacity="1" />
          <stop offset="55%" stopColor={palette.outer} stopOpacity="0.95" />
          <stop offset="100%" stopColor={palette.vein} stopOpacity="0.8" />
        </radialGradient>
      </defs>
      {/* Realistic rose petal shape */}
      <path
        d="M13 2 C9.5 0.5, 3 5, 2.5 11.5 C2 18, 5.5 24.5, 9.5 27 C11 27.8, 12.2 28, 13 28 C13.8 28, 15 27.8, 16.5 27 C20.5 24.5, 24 18, 23.5 11.5 C23 5, 16.5 0.5, 13 2Z"
        fill={`url(#${uid})`}
        opacity="0.95"
      />
      {/* Subtle top notch */}
      <path d="M11.8 3.2 C12.3 2, 13 1.8, 14.2 3.2" stroke={palette.vein} strokeWidth="0.5" strokeOpacity="0.35" fill="none" />
      {/* Center vein */}
      <line x1="13" y1="2.5" x2="13" y2="27" stroke="white" strokeWidth="0.8" strokeOpacity="0.45" />
      {/* Side veins */}
      <path d="M13 9 Q17.5 11.5 18.5 15" stroke="white" strokeWidth="0.45" strokeOpacity="0.3" fill="none" />
      <path d="M13 9 Q8.5 11.5 7.5 15" stroke="white" strokeWidth="0.45" strokeOpacity="0.3" fill="none" />
      <path d="M13 16 Q18 18.5 19 22" stroke="white" strokeWidth="0.35" strokeOpacity="0.22" fill="none" />
      <path d="M13 16 Q8 18.5 7 22" stroke="white" strokeWidth="0.35" strokeOpacity="0.22" fill="none" />
      {/* Silky highlight */}
      <ellipse cx="10.5" cy="9" rx="2.8" ry="5.5" fill="white" opacity="0.14" transform="rotate(-12 10.5 9)" />
    </svg>
  );
}

function GoldParticle({ size, left, delay, duration }: { size: number; left: string; delay: string; duration: string }) {
  return (
    <div
      className="gold-particle"
      style={{
        width: size,
        height: size,
        left,
        animation: `gold-float ${duration} ${delay} ease-in infinite`,
        opacity: 0,
      }}
    />
  );
}

function FlyingPetals() {
  const petalConfigs: PetalConfig[] = buildPetalConfigs(PETAL_COUNT, PETAL_SPEED);
  // ~5 golden particles
  const goldCount = Math.max(3, Math.round(PETAL_COUNT * 0.14));

  return (
    <div
      className="pointer-events-none"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        overflow: 'hidden',
        background: 'transparent',
      }}
    >
      {petalConfigs.map((cfg, i) => (
        <div
          key={i}
          className={`petal-particle ${cfg.depth === 0 ? 'petal-particle-close' : cfg.depth === 2 ? 'petal-particle-far' : ''}`}
          style={{
            left: cfg.left,
            opacity: 0,
            animation: `${cfg.anim} ${cfg.duration} ${cfg.delay} ease-in-out infinite, petal-sway ${cfg.duration} ${cfg.delay} ease-in-out infinite`,
          }}
        >
          <PetalSVG palette={cfg.palette} size={cfg.size} />
        </div>
      ))}
      {Array.from({ length: goldCount }).map((_, i) => (
        <GoldParticle
          key={`g-${i}`}
          size={2 + (i % 3)}
          left={`${((i * 19 + 7) % 95).toFixed(0)}%`}
          delay={`${((i * 1.8) % 10).toFixed(1)}s`}
          duration={`${(18 + (i % 7)) * (SPEED_MAP[PETAL_SPEED] ?? 1.0)}s`}
        />
      ))}
    </div>
  );
}

function FloralDivider() {
  return (
    <div className="flex items-center justify-center gap-3 my-6">
      <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, #c9a96e)' }} />
      <span className="text-lg lux-float" style={{ color: '#c9a96e' }}>✦</span>
      <span className="text-sm" style={{ color: '#c9a96e' }}>❧</span>
      <span className="text-lg lux-float" style={{ color: '#c9a96e', animationDelay: '0.5s' }}>✦</span>
      <div className="h-px flex-1" style={{ background: 'linear-gradient(to left, transparent, #c9a96e)' }} />
    </div>
  );
}

function OrnamentTop() {
  return (
    <div className="flex justify-center mb-4">
      <svg width="120" height="24" viewBox="0 0 120 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60 2 C50 2, 20 12, 2 12 C20 12, 50 22, 60 22 C70 22, 100 12, 118 12 C100 12, 70 2, 60 2Z" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.6"/>
        <circle cx="60" cy="12" r="3" fill="var(--secondary)" opacity="0.7"/>
        <circle cx="20" cy="12" r="1.5" fill="var(--secondary)" opacity="0.5"/>
        <circle cx="100" cy="12" r="1.5" fill="var(--secondary)" opacity="0.5"/>
      </svg>
    </div>
  );
}

// AV Logo for pages 2-5
function AVLogo() {
  return (
    <div className="flex justify-center mb-4 lux-fade-in">
      <Image
        src="/assets/images/Logo_Vimi-1786996384061.png"
        alt="AV Logo"
        width={90}
        height={60}
        unoptimized
        className="h-12 w-auto object-contain"
      />
    </div>
  );
}

// Page 1: Cover
function CoverPage() {
  return (
    <div className="flex flex-col items-center text-center px-6 py-8 min-h-full justify-between">
      <div className="w-full">
        {/* Shree Ganeshaye Namah */}
        <div className="mb-3 lux-heading">
          <p className="font-display text-base font-semibold tracking-wider lux-shimmer-text">
            ॥ श्री गणेशाय नमः ॥
          </p>
        </div>

        {/* Ganesha Image */}
        <div className="flex justify-center mb-3 lux-fade-in">
          <Image
            src="/assets/images/Ganesha-1787000120210.png"
            alt="Shree Ganesha"
            width={80}
            height={95}
            unoptimized
            priority
            className="w-auto object-contain"
            style={{ maxHeight: '95px' }}
          />
        </div>

        <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-3 lux-heading-delay" style={{ color: 'var(--secondary)' }}>
          Together with their families
        </p>

        {/* Groom */}
        <div className="mb-2 lux-card-1">
          <h1 className="font-display text-3xl sm:text-4xl font-medium" style={{ color: 'var(--foreground)' }}>
            Vikas Gawande
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            S/o Shri Manikrao Gawande
          </p>
        </div>

        <p className="font-display text-xl my-2 lux-shimmer-text lux-card-2">&amp;</p>

        {/* Bride */}
        <div className="mb-4 lux-card-3">
          <h1 className="font-display text-3xl sm:text-4xl font-medium" style={{ color: 'var(--foreground)' }}>
            Anamika Panse
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            D/o Late. Shri Kishorrao Panse
          </p>
        </div>

        <p className="text-sm tracking-widest uppercase mb-4 lux-card-4" style={{ color: 'var(--muted-foreground)' }}>
          #Vimi
        </p>
      </div>

      <div className="w-full rounded-2xl overflow-hidden shadow-lg mb-4 lux-card-5" style={{ border: '1px solid var(--border)', background: '#fdf0e8' }}>
        <Image
          src="/assets/images/Final_photo_2-1786996888211.jpg"
          alt="Vikas and Anamika together"
          width={600}
          height={800}
          unoptimized
          priority
          className="w-full object-contain"
          style={{ maxHeight: '420px' }}
        />
      </div>

      <div className="lux-fade-in" style={{ animationDelay: '0.6s' }}>
        <p className="font-display text-base sm:text-lg mb-2" style={{ color: 'var(--foreground)' }}>
          Request the pleasure of your company
        </p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          as we celebrate our engagement &amp; wedding
        </p>

        <FloralDivider />

        <div className="flex justify-center gap-8 text-sm">
          <div className="text-center">
            <p className="font-semibold" style={{ color: 'var(--foreground)' }}>31 Dec 2026</p>
            <p style={{ color: 'var(--muted-foreground)' }}>Bhopal</p>
          </div>
          <div className="w-px" style={{ background: 'var(--border)' }} />
          <div className="text-center">
            <p className="font-semibold" style={{ color: 'var(--foreground)' }}>4 Jan 2027</p>
            <p style={{ color: 'var(--muted-foreground)' }}>Warud</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Page 2: Engagement Details
function EngagementPage() {
  return (
    <div className="flex flex-col px-6 py-8 min-h-full">
      <AVLogo />

      <div className="text-center mb-6">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase mb-2 lux-heading" style={{ color: 'var(--secondary)' }}>
          First Celebration
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-medium mb-1 lux-heading-delay lux-shimmer-text">
          💍 Engagement
        </h2>
        <p className="font-display text-base lux-card-1" style={{ color: 'var(--secondary)' }}>
          Ring Ceremony &amp; Celebration
        </p>
      </div>

      <FloralDivider />

      {/* Date & Time */}
      <div className="rounded-2xl p-5 mb-4 lux-card-1" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">📅</div>
          <div>
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Date &amp; Time</p>
            <p className="font-display text-xl" style={{ color: 'var(--primary)' }}>31 December 2026</p>
            <p className="text-sm mt-1 font-medium" style={{ color: 'var(--secondary)' }}>12:00 PM onwards</p>
            <p className="text-xs mt-1 italic" style={{ color: 'var(--muted-foreground)' }}>New Year's Eve — a day to remember forever ✨</p>
          </div>
        </div>
      </div>

      {/* Venue */}
      <div className="rounded-2xl p-5 mb-4 lux-card-2" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">📍</div>
          <div className="flex-1">
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Venue</p>
            <p className="font-display text-lg" style={{ color: 'var(--primary)' }}>Royal Celebrations</p>
            <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Bhopal, Madhya Pradesh</p>
            <a
              href="https://maps.app.goo.gl/o6Wyuiqrn5Wi5iZo7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:underline transition-colors"
              style={{ color: 'var(--primary)' }}
            >
              View on Maps →
            </a>
          </div>
        </div>
      </div>

      {/* Baarat Departure */}
      <div className="rounded-2xl p-5 mb-4 lux-card-3" style={{ backgroundColor: 'rgba(255,240,230,0.93)', border: '1px solid rgba(201,169,110,0.5)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">🚗</div>
          <div>
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Baarat Departure</p>
            <p className="text-sm font-medium" style={{ color: 'var(--primary)' }}>2 January 2027 · 2:00 PM</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>From Groom's Home Address, Bhopal, Madhya Pradesh</p>
            <p className="text-xs mt-0.5 italic" style={{ color: 'var(--muted-foreground)' }}>Proceeding to Warud, Maharashtra (~350 km)</p>
          </div>
        </div>
      </div>

      {/* Dress Code */}
      <div className="rounded-2xl p-5 mb-4 lux-card-4" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">👗</div>
          <div>
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Dress Code</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Indo Western</p>
          </div>
        </div>
      </div>

      <FloralDivider />

      {/* RSVP Contacts */}
      <div className="rounded-2xl p-4 text-center lux-card-5" style={{ backgroundColor: 'rgba(255,240,230,0.93)', border: '1px solid rgba(201,169,110,0.5)' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>RSVP — Groom Side</p>
        <div className="flex justify-center gap-6 text-sm">
          <a href="tel:8878550554" className="font-medium hover:underline" style={{ color: 'var(--primary)' }}>📞 8878550554</a>
          <a href="tel:9004520070" className="font-medium hover:underline" style={{ color: 'var(--primary)' }}>📞 9004520070</a>
        </div>
      </div>
    </div>
  );
}

// Page 3: Wedding Details
function WeddingPage() {
  return (
    <div className="flex flex-col px-6 py-8 min-h-full">
      <AVLogo />

      <div className="text-center mb-6">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase mb-2 lux-heading" style={{ color: 'var(--secondary)' }}>
          The Big Day
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-medium mb-1 lux-heading-delay lux-shimmer-text">
          ❤️ Wedding
        </h2>
        <p className="font-display text-base lux-card-1" style={{ color: 'var(--secondary)' }}>
          Vivaah Ceremony
        </p>
      </div>

      <FloralDivider />

      {/* Date & Time */}
      <div className="rounded-2xl p-5 mb-4 lux-card-1" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">📅</div>
          <div>
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Date &amp; Time</p>
            <p className="font-display text-xl" style={{ color: 'var(--primary)' }}>4 January 2027</p>
            <p className="text-sm mt-1 font-medium" style={{ color: 'var(--secondary)' }}>10:30 AM onwards</p>
            <p className="text-xs mt-1 italic" style={{ color: 'var(--muted-foreground)' }}>Auspicious morning ceremony 🌅</p>
          </div>
        </div>
      </div>

      {/* Venue */}
      <div className="rounded-2xl p-5 mb-4 lux-card-2" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">📍</div>
          <div className="flex-1">
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Venue</p>
            <p className="font-display text-lg" style={{ color: 'var(--primary)' }}>Ramdevbaba Mangalam, Lawn</p>
            <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Warud, Maharashtra</p>
            <a
              href="https://maps.app.goo.gl/msDE3717cPhYMEgu5"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:underline transition-colors"
              style={{ color: 'var(--primary)' }}
            >
              View on Maps →
            </a>
          </div>
        </div>
      </div>

      {/* Dress Code */}
      <div className="rounded-2xl p-5 mb-4 lux-card-3" style={{ backgroundColor: 'rgba(255,240,230,0.93)', border: '1px solid rgba(201,169,110,0.5)' }}>
        <div className="flex items-start gap-4">
          <div className="text-2xl mt-0.5">👗</div>
          <div>
            <p className="font-semibold text-base mb-0.5" style={{ color: 'var(--foreground)' }}>Dress Code</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Traditional</p>
          </div>
        </div>
      </div>

      <FloralDivider />

      <div className="text-center mt-4 lux-card-5">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          A traditional ceremony in the heart of Maharashtra — we can't wait to celebrate with you.
        </p>
      </div>
    </div>
  );
}

// Page 4: Travel & Stay Info
function TravelPage() {
  return (
    <div className="flex flex-col px-6 py-8 min-h-full">
      <AVLogo />

      <div className="text-center mb-6">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase mb-2 lux-heading" style={{ color: 'var(--secondary)' }}>
          Getting Here
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-medium mb-1 lux-heading-delay lux-shimmer-text">
          🗺️ Travel &amp; Stay
        </h2>
        <p className="font-display text-base lux-card-1" style={{ color: 'var(--secondary)' }}>
          Everything you need to know
        </p>
      </div>

      <FloralDivider />

      {/* Bhopal Travel */}
      <div className="mb-4 lux-card-2">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>1</div>
          <p className="font-semibold" style={{ color: 'var(--foreground)' }}>Bhopal — Engagement (31 Dec)</p>
        </div>
        <div className="rounded-2xl p-4 space-y-2" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
          <div className="flex items-start gap-2 text-sm">
            <span>📍</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Venue: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Royal Celebrations, Bhopal, MP · 12:00 PM</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>✈️</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>By Air: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Raja Bhoj Airport (BHO) — well connected from major cities</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🚆</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>By Train: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Bhopal Junction (BPL) — major railway hub</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🚗</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Baarat Departure: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>2 Jan 2027 at 2:00 PM from Groom's home, Bhopal</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🏨</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Stay: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Accommodation assistance available — let us know in your RSVP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Warud Travel */}
      <div className="mb-4 lux-card-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--secondary)', color: 'white' }}>2</div>
          <p className="font-semibold" style={{ color: 'var(--foreground)' }}>Warud — Wedding (4 Jan)</p>
        </div>
        <div className="rounded-2xl p-4 space-y-2" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
          <div className="flex items-start gap-2 text-sm">
            <span>📍</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Venue: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Ramdevbaba Mangalam, Lawn, Warud, MH · 10:30 AM</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>✈️</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Nearest Airport: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Nagpur Airport (NAG) — ~90 km from Warud</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🚆</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>By Train: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>Nagpur Railway Station — cab/bus available to Warud</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🚗</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Pickup: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>We can arrange pickup from Nagpur — mention in RSVP</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span>🏨</span>
            <div>
              <span className="font-medium" style={{ color: 'var(--foreground)' }}>Stay: </span>
              <span style={{ color: 'var(--muted-foreground)' }}>On-site accommodation available — share requirements in RSVP</span>
            </div>
          </div>
        </div>
      </div>

      <FloralDivider />

      <div className="rounded-2xl p-4 text-center lux-card-4" style={{ backgroundColor: 'rgba(255,240,230,0.93)', border: '1px solid rgba(201,169,110,0.5)' }}>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          🙏 We want to make your journey as comfortable as possible. Please fill in your travel details in the RSVP form so we can plan accordingly.
        </p>
      </div>
    </div>
  );
}

// Page 5: RSVP CTA
function RSVPPage({ rsvpPath }: { rsvpPath: string }) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-8 min-h-full justify-between">
      <div className="w-full">
        <AVLogo />

        <p className="text-xs font-semibold tracking-[0.25em] uppercase mb-3 lux-heading" style={{ color: 'var(--secondary)' }}>
          You're Invited
        </p>

        <h2 className="font-display text-3xl sm:text-4xl font-medium mb-3 lux-heading-delay lux-shimmer-text">
          Will you join us?
        </h2>

        <p className="font-display text-lg mb-6 lux-card-1" style={{ color: 'var(--secondary)' }}>
          We'd love to celebrate with you ❤️
        </p>

        <FloralDivider />

        <div className="rounded-2xl p-5 mb-4 text-left lux-card-2" style={{ backgroundColor: 'rgba(255,248,240,0.92)', border: '1px solid rgba(201,169,110,0.4)' }}>
          <p className="font-semibold text-sm mb-3 text-center" style={{ color: 'var(--foreground)' }}>Quick Summary</p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-lg">💍</span>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Engagement — 31 Dec 2026</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>12:00 PM · Royal Celebrations, Bhopal, MP</p>
              </div>
            </div>
            <div className="h-px" style={{ background: 'rgba(201,169,110,0.3)' }} />
            <div className="flex items-start gap-3">
              <span className="text-lg">🚗</span>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Baarat Departure — 2 Jan 2027</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>2:00 PM · From Groom's Home, Bhopal, MP</p>
              </div>
            </div>
            <div className="h-px" style={{ background: 'rgba(201,169,110,0.3)' }} />
            <div className="flex items-start gap-3">
              <span className="text-lg">❤️</span>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Wedding — 4 Jan 2027</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>10:30 AM · Ramdevbaba Mangalam, Lawn, Warud, MH</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="rounded-2xl p-4 mb-4 lux-card-3" style={{ backgroundColor: 'rgba(255,240,230,0.93)', border: '1px solid rgba(201,169,110,0.5)' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>For Queries</p>
          <div className="flex justify-center gap-6 text-xs">
            <div>
              <a href="tel:8878550554" className="block hover:underline" style={{ color: 'var(--primary)' }}>📞 8878550554</a>
              <a href="tel:9004520070" className="block hover:underline" style={{ color: 'var(--primary)' }}>📞 9004520070</a>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full space-y-4 lux-card-4">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Please let us know if you can make it, and share your travel &amp; accommodation preferences so we can plan everything comfortably for you.
        </p>

        <Link
          href={rsvpPath}
          className="block w-full py-4 px-6 rounded-2xl text-center font-semibold text-base transition-all duration-200 hover:opacity-90 active:scale-95 shadow-md"
          style={{
            background: 'linear-gradient(135deg, #c9a96e 0%, #e8c98a 100%)',
            color: '#fff',
          }}
        >
          RSVP Now →
        </Link>

        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Kindly respond by <span className="font-semibold">15 December 2026</span>
        </p>
      </div>
    </div>
  );
}

export default function InvitationCard({ rsvpPath = '/rsvp' }: { rsvpPath?: string }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [musicReady, setMusicReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Restore mute preference from localStorage
    const savedMute = typeof window !== 'undefined' ? localStorage.getItem('weddingMusicMuted') : null;
    const startMuted = savedMute === 'false' ? false : true; // default muted for autoplay policy

    // Primary: raw GitHub URL (actual file location), then local path as fallback
    const musicSources = [
      'https://raw.githubusercontent.com/rjcorporatetrainer-oss/vimi/main/public/assets/music/background.mp3',
      '/assets/music/background.mp3',
      '/assets/music/music.mp3',
    ];

    let currentAudio: HTMLAudioElement | null = null;
    let stopped = false;
    let interactionHandlerAttached = false;

    const tryPlay = (audio: HTMLAudioElement) => {
      audio.play().then(() => {
        setIsPlaying(true);
        setMusicReady(true);
      }).catch(() => {
        // Autoplay blocked — music ready but waiting for interaction
        setMusicReady(true);
      });
    };

    const setupAudio = (src: string) => {
      const audio = new Audio(src);
      audio.loop = true;
      audio.volume = 0.35;
      audio.muted = true; // always start muted for autoplay policy compliance
      currentAudio = audio;
      audioRef.current = audio;

      audio.addEventListener('canplaythrough', () => {
        if (stopped) return;
        tryPlay(audio);
      }, { once: true });

      audio.addEventListener('error', () => {
        if (stopped) return;
        const nextIndex = musicSources.indexOf(src) + 1;
        if (nextIndex < musicSources.length) {
          setupAudio(musicSources[nextIndex]);
        } else {
          // All sources failed — still mark ready so UI shows
          setMusicReady(true);
        }
      }, { once: true });
    };

    setupAudio(musicSources[0]);

    // First user interaction: unmute and ensure playback starts
    const handleFirstInteraction = () => {
      if (interactionHandlerAttached) return;
      interactionHandlerAttached = true;
      const audio = audioRef.current;
      if (!audio) return;

      const savedPref = localStorage.getItem('weddingMusicMuted');
      // Only unmute if user hasn't explicitly muted before
      if (savedPref !== 'true') {
        audio.muted = false;
        setIsMuted(false);
        localStorage.setItem('weddingMusicMuted', 'false');
      }

      // Always attempt play on first interaction (handles autoplay block)
      if (audio.paused) {
        audio.play().then(() => {
          setIsPlaying(true);
          setMusicReady(true);
        }).catch(() => {
          setMusicReady(true);
        });
      } else {
        setIsPlaying(true);
        setMusicReady(true);
      }
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });

    return () => {
      stopped = true;
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.src = '';
      }
    };
  }, []);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
      localStorage.setItem('weddingMusicMuted', 'false');
      // If not playing yet, start playing
      if (!isPlaying || audio.paused) {
        audio.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    } else {
      audio.muted = true;
      setIsMuted(true);
      localStorage.setItem('weddingMusicMuted', 'true');
    }
  };

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.muted = false;
      setIsMuted(false);
      localStorage.setItem('weddingMusicMuted', 'false');
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const pages = [
    { label: 'Welcome', component: <CoverPage /> },
    { label: 'Engagement', component: <EngagementPage /> },
    { label: 'Wedding', component: <WeddingPage /> },
    { label: 'Travel', component: <TravelPage /> },
    { label: 'RSVP', component: <RSVPPage rsvpPath={rsvpPath} /> },
  ];

  const goTo = (index: number) => {
    if (animating || index === currentPage) return;
    setDirection(index > currentPage ? 'forward' : 'back');
    setAnimating(true);
    setTimeout(() => {
      setCurrentPage(index);
      setAnimating(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 250);
  };

  const goNext = () => { if (currentPage < TOTAL_PAGES - 1) goTo(currentPage + 1); };
  const goPrev = () => { if (currentPage > 0) goTo(currentPage - 1); };

  const [touchStart, setTouchStart] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start py-6 px-3" style={{ backgroundColor: '#fdf6f0' }}>
      {/* Inject luxury animation keyframes */}
      <style dangerouslySetInnerHTML={{ __html: luxuryStyles }} />

      {/* Card */}
      <div
        className="w-full max-w-sm relative overflow-hidden rounded-3xl shadow-2xl"
        style={{
          background: 'linear-gradient(160deg, #fdf8f2 0%, #fef3e8 35%, #fdf0e8 65%, #faf5ee 100%)',
          border: '2px solid #c9a96e',
          minHeight: '600px',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Minimal radial glow — subtle warmth at center */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(255,240,210,0.45) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />

        {/* Flying petals and dried leaves — above all content */}
        <FlyingPetals />

        {/* Top border accent */}
        <div className="h-1 w-full relative z-10" style={{ background: 'linear-gradient(90deg, #c9a96e, #e8c98a, #c9a96e)' }} />

        {/* Page content */}
        <div
          className="relative z-10 transition-all duration-250"
          style={{
            opacity: animating ? 0 : 1,
            transform: animating
              ? `translateX(${direction === 'forward' ? '20px' : '-20px'})`
              : 'translateX(0)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
          }}
        >
          {pages[currentPage].component}
        </div>

        {/* Bottom border accent */}
        <div className="h-1 w-full relative z-10" style={{ background: 'linear-gradient(90deg, #c9a96e, #e8c98a, #c9a96e)' }} />
      </div>

      {/* Page indicator dots */}
      <div className="flex items-center gap-2 mt-5">
        {pages.map((page, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to ${page.label}`}
            className="transition-all duration-200"
            style={{
              width: i === currentPage ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: i === currentPage ? '#c9a96e' : 'var(--border)',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          />
        ))}
      </div>

      {/* Page label */}
      <p className="mt-2 text-xs tracking-widest uppercase" style={{ color: 'var(--muted-foreground)' }}>
        {pages[currentPage].label} · {currentPage + 1} / {TOTAL_PAGES}
      </p>

      {/* Navigation buttons */}
      <div className="flex items-center gap-4 mt-4 w-full max-w-sm">
        <button
          onClick={goPrev}
          disabled={currentPage === 0}
          className="flex-1 py-3 rounded-xl text-sm font-medium transition-all duration-200 disabled:opacity-30"
          style={{
            backgroundColor: 'rgba(255,255,255,0.85)',
            color: 'var(--foreground)',
            border: '1px solid #c9a96e',
          }}
        >
          ← Previous
        </button>

        {currentPage < TOTAL_PAGES - 1 ? (
          <button
            onClick={goNext}
            className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg, #c9a96e, #e8c98a)',
              color: '#fff',
              border: 'none',
            }}
          >
            Next →
          </button>
        ) : (
          <Link
            href={rsvpPath}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-center transition-all duration-200 hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg, #c9a96e, #e8c98a)',
              color: '#fff',
            }}
          >
            RSVP Now →
          </Link>
        )}
      </div>

      {/* Swipe hint */}
      <p className="mt-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
        Swipe or tap to navigate
      </p>
    </div>
  );
}
